import { PlusOutlined } from "@ant-design/icons";
import { Button, Collapse, Input, Modal, Pagination, Tag } from "antd";
import React, { useState } from "react";
import { $t } from "~/i18n.js";
import { api } from "~/lib/api.js";
import { AddButton } from "../buttons/AddButton.jsx";
import { TimeFormat } from "../date/TimeFormat.jsx";
import { VoucherBalanceStatus } from "./VoucherBalanceStatus.jsx";
import { VoucherInfo } from "./VoucherInfo.jsx";
import { VoucherModal } from "./VoucherModal.jsx";

/**
 * VoucherSelector component
 * @param {object} props
 * @param {object} props.readOnly - If ReadOnly
 * @param {object} props.flight - The flight object
 * @param {object} props.sleepFlight - The sleepFlight object
 * @param {function} props.saveFlight - Function to save flight
 * @param {function} props.saveSleepFlight - Function to save sleepFlight
 */
export function VoucherSelector({
  readOnly,
  flight,
  sleepFlight,
  saveFlight,
  saveSleepFlight,
  size='default'
}) {
  const [modalOpen, setModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [vouchers, setVouchers] = useState([]);
  const [modalVoucher, setModalVoucher] = useState(null);
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [serverSideMode, setServerSideMode] = useState(false);
  const [activeSearch, setActiveSearch] = useState("");

  const fetchVouchers = async (page = 1, search = searchText) => {
    setLoading(true);
    let url = `financial/vouchers.json?page=${page}&open=1&date=${flight.datum}`;
    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }
    const data = await api.get(url);
    setVouchers(data?.results || []);
    setTotalCount(data?.count || 0);
    setLoading(false);
  };

  const openModal = async () => {
    setModalOpen(true);
    setCurrentPage(1);
    setSearchText("");
    setLoading(true);
    const url = `financial/vouchers.json?page=1&open=1&date=${flight.datum}`;
    const data = await api.get(url);
    const results = data?.results || [];
    const count = data?.count || 0;
    setVouchers(results);
    setTotalCount(count);
    // If all results fit on the first page, use fast client-side filtering
    setServerSideMode(count > results.length);
    setActiveSearch("");
    setLoading(false);
  };

  const closeModal = () => {
    setModalOpen(false);
    setSearchText("");
    setActiveSearch("");
    setCurrentPage(1);
  };

  const currentVoucher = flight?.voucher || sleepFlight?.voucher;
  const hasVoucher = currentVoucher && currentVoucher.id !== 'DELETE';
  const voucherIdFlight = flight?.voucher?.id;
  const voucherIdSleep = sleepFlight?.voucher?.id;

  const voucherMismatch =
    voucherIdFlight && voucherIdSleep && voucherIdFlight !== voucherIdSleep;

  const handleSelect = async (voucher) => {
    setLoading(true);
    // ps, we can set the betalend lid irrespective of the voucher
    await saveFlight({
      ...flight,
      voucher,
      voucher_id: voucher.id
    });
    if (sleepFlight)
      await saveSleepFlight({
        ...sleepFlight,
        voucher,
        voucher_id: voucher.id
      });
    setLoading(false);
    setModalOpen(false);
  };

  const handleShowDetails = (voucher, e) => {
    if (e) e.stopPropagation();
    setModalVoucher(voucher);
  };

  const handleSearch = (value) => {
    setCurrentPage(1);
    setActiveSearch(value);
    fetchVouchers(1, value);
  };

  const displayedVouchers = serverSideMode
    ? vouchers
    : vouchers.filter((v) => {
        if (!searchText) return true;
        const s = searchText.toLowerCase();
        return v.name?.toLowerCase().includes(s) || v.gift_name?.toLowerCase().includes(s);
      });

  return (
    <section>
      {voucherMismatch && <Tag color="red">{$t("Bon niet gekoppeld")}</Tag>}

      <div>
        {!hasVoucher ? (
          <AddButton disabled={readOnly} onClick={openModal} size={size} />
        ) : (
          <Button
            type='dashed'
            size={size}
            onClick={() => handleShowDetails(currentVoucher)}
            style={{ width: 100 }}
          >
            {currentVoucher?.name?.substring(0, 10) || $t("Bon")}
          </Button>
        )}
        <Modal
          open={modalOpen}
          onCancel={closeModal}
          footer={null}
          loading={loading}
          title={$t('Selecteer bon')}
        >
          <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
            <Input
              placeholder={$t("Zoek op naam of e-mail...")}
              value={searchText}
              onChange={(e) => {
                setSearchText(e.target.value);
                if (!serverSideMode) return;
                if (!e.target.value) handleSearch("");
              }}
              onPressEnter={serverSideMode ? () => handleSearch(searchText) : undefined}
              allowClear
            />
            {serverSideMode && (
              <Button
                type={searchText !== activeSearch ? "primary" : "default"}
                disabled={searchText === activeSearch}
                loading={loading}
                onClick={() => handleSearch(searchText)}
              >
                {$t("Zoek")}
              </Button>
            )}
          </div>
          {displayedVouchers.length >= 1 && (
            <Collapse accordion items={displayedVouchers.map((voucher) => ({
              key: voucher.id,
              label: (
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 12,
                      }}
                    >
                      <VoucherBalanceStatus balance={voucher.ledger_balance} />
                      <span style={{ marginRight: 16 }}>
                        { voucher.reserved_timeslot
                           ? <TimeFormat date={voucher.reserved_timeslot} />
                           : <Tag>{$t('flex')}</Tag>
                        }
                      </span>
                      <span style={{ fontWeight: 500, marginRight: 16 }}>
                        {voucher.prepaid_product?.name}
                      </span>
                      <span style={{ color: "#888" }}>{voucher.gift_name ? (" 🎁 " + voucher.gift_name) : voucher.name }</span>
                    </div>
              ),
              extra: (
                    <Button
                      type="primary"
                      shape="circle"
                      onClick={() => handleSelect(voucher)}
                      icon={<PlusOutlined />}
                    />
              ),
              children: (
                <>
                  <VoucherInfo voucher={voucher} />
                  <Button
                    type="primary"
                    style={{ marginTop: 12 }}
                    onClick={() => handleSelect(voucher)}
                    icon={<PlusOutlined />}
                    block
                  >
                    {$t("Koppel deze bon")}
                  </Button>
                </>
              ),
            }))}
            />
          )}
          {serverSideMode && totalCount > 0 && (
            <Pagination
              current={currentPage}
              total={totalCount}
              pageSize={100}
              size="small"
              showSizeChanger={false}
              style={{ marginTop: 12, textAlign: "center" }}
              onChange={(page) => {
                setCurrentPage(page);
                fetchVouchers(page, searchText);
              }}
            />
          )}
        </Modal>
        <VoucherModal
          open={!!modalVoucher}
          voucher={modalVoucher}
          fetchRemainingData={true}
          onClose={() => setModalVoucher(null)}
          disconnectVoucher={() => handleSelect({ id: "DELETE" })} // Disconnect voucher
          readOnly={readOnly}
        />
      </div>
    </section>
  );
}
